import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faUserGroup,
    faCheckCircle,
    faClock,
    faExclamationTriangle,
    faUser,
    faLaptop,
    faMobileAlt,
    faHistory
} from "@fortawesome/free-solid-svg-icons";
import { primaryColor, secondaryColor } from "../../../../../utils/comon";
import MetricCard from "../../../../common/Card/MetricCard";
import CommonModal from "../../../../common/commonmodal/CommonModal";
import PageHeadingSection from "../../../../common/pageheading/PageHeadingSection";

import CommonTable from "../../../../common/table/ComonTable";
import Breadcrumbs from "../../../../common/BreadCrum/BreadCrum";
import apiServices from "../../../../../services/apiService";

const AttendanceList = () => {
    const { user } = useSelector((state) => state.auth);

    const getTodayDateString = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, "0");
        const day = String(today.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const getTomorrowDateString = () => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const year = tomorrow.getFullYear();
        const month = String(tomorrow.getMonth() + 1).padStart(2, "0");
        const day = String(tomorrow.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const [startDate, setStartDate] = useState(getTodayDateString());
    const [endDate, setEndDate] = useState(getTomorrowDateString());
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");

    useEffect(() => {
        const fetchAttendanceReport = async () => {
            try {
                setLoading(true);
                const siteId = user?.siteId;
                const unitId = user?.branchorunitID;

                // Fetch attendance report with dynamic parameters
                const response = await apiServices({
                    method: "GET",
                    url: `/Residential/ResAttendanceManagement/report?siteId=${siteId}&unitId=${unitId}&startDate=${startDate}&endDate=${endDate}`
                });

                if (response?.success) {
                    const apiData = response.data.Data;
                    setReportData(apiData);
                    setPagination(prev => ({ ...prev, totalRecords: apiData.length }));
                }
            } catch (error) {
                console.error("Error fetching CommAttendanceManagement report:", error);
            } finally {
                setLoading(false);
            }
        };

        if (user && startDate && endDate) {
            fetchAttendanceReport();
        }
    }, [user, startDate, endDate]);

    const [pagination, setPagination] = useState({
        pageNumber: 1,
        pageSize: 10,
        totalRecords: 6
    });



    const mapApiRecord = (apiRecord, index) => {
        const formatTime = (isoString) => {
            if (!isoString) return "—";
            const date = new Date(isoString);
            if (isNaN(date.getTime())) return "—";
            return date.toLocaleTimeString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: true
            });
        };

        const formatDate = (isoString) => {
            if (!isoString) return "—";
            return isoString.split("T")[0];
        };

        const punchHistory = (apiRecord.Scans || []).map((scan) => ({
            time: formatTime(scan.ScanTime),
            type: scan.EventType || "—",
            device: `${scan.ScanMethodName || "Scan"} (${scan.DeviceCode || "N/A"})`
        }));

        const statusNorm = (apiRecord.Status || "").toUpperCase();

        return {
            attendanceId: apiRecord.Id || `api-${index}`,
            employeeName: apiRecord.UserName || "—",
            role: "Employee",
            date: formatDate(apiRecord.AttendanceDate),
            checkIn: formatTime(apiRecord.FirstInTime),
            checkOut: formatTime(apiRecord.LastOutTime),
            workHours: apiRecord.TotalWorkedHoursFormatted || "—",
            status: statusNorm === "SHORTFALL" ? "SHORTFALL" : statusNorm,
            punchSource: apiRecord.Scans?.[0]?.ScanMethodName || "—",
            ipAddress: "N/A",
            location: apiRecord.Scans?.[0]?.DeviceCode ? `Device: ${apiRecord.Scans[0].DeviceCode}` : "—",
            unitName: apiRecord.UnitName || apiRecord.unitName || apiRecord.BranchOrUnitName || apiRecord.branchOrUnitName || user?.branchorunitName || "—",
            punchHistory: punchHistory,
            earlyLeaveMins: apiRecord.EarlyLeaveByMins,
            extraTimeMins: apiRecord.ExtraTimeMins
        };
    };

    const attendanceRecords = Array.isArray(reportData) ? reportData.map((r, idx) => mapApiRecord(r, idx)) : [];

    const filteredAttendance = attendanceRecords.filter((record) => {
        const query = searchQuery.toLowerCase();
        return (
            (record.employeeName || "").toLowerCase().includes(query) ||
            (record.unitName || "").toLowerCase().includes(query) ||
            (record.status || "").toLowerCase().includes(query)
        );
    });

    const totalEmployees = attendanceRecords.length;
    const presentToday = attendanceRecords.filter(r => r.status === "PRESENT" || r.status === "LATE" || r.status === "HALF_DAY" || r.status === "SHORTFALL").length;
    const lateCheckins = attendanceRecords.filter(r => r.status === "LATE").length;
    const onLeave = attendanceRecords.filter(r => r.status === "ON_LEAVE").length;

    const metricCardsData = [
        {
            title: "TOTAL USERS",
            value: String(totalEmployees).padStart(2, "0"),
            icon: faUserGroup
        },
        {
            title: "PRESENT TODAY",
            value: `${String(presentToday).padStart(2, "0")} (${totalEmployees > 0 ? Math.round((presentToday / totalEmployees) * 100) : 0}%)`,
            icon: faCheckCircle
        },
        {
            title: "LATE COMERS",
            value: String(lateCheckins).padStart(2, "0"),
            icon: faClock
        },
        {
            title: "ON LEAVE",
            value: String(onLeave).padStart(2, "0"),
            icon: faExclamationTriangle
        }
    ];

    const tableHeaders = [
        { label: "NO", key: "no" },
        { label: "EMPLOYEE", key: "employee", align: "left" },
        { label: "ATTENDANCE DATE", key: "date" },
        { label: "CHECK IN", key: "checkIn" },
        { label: "CHECK OUT", key: "checkOut" },
        { label: "WORK HOURS", key: "workHours" },
        { label: "STATUS", key: "status" }
    ];

    const getStatusStyles = (status) => {
        switch (status) {
            case "PRESENT": return "bg-[#DCFCE7] text-[#166534]";
            case "LATE": return "bg-[#FEF9C3] text-[#D97706]";
            case "HALF_DAY": return "bg-[#DBEAFE] text-[#1D4ED8]";
            case "ABSENT": return "bg-[#E31F262B] text-[#E31F26]";
            case "ON_LEAVE": return "bg-[#F1F5F9] text-[#475569]";
            case "SHORTFALL": return "bg-orange-100 text-orange-700 font-bold border border-orange-200";
            default: return "bg-gray-100 text-gray-700";
        }
    };

    const getSourceIcon = (source) => {
        if (source?.toLowerCase().includes("web")) return faLaptop;
        if (source?.toLowerCase().includes("mobile")) return faMobileAlt;
        return faUser;
    };

    const renderTableCell = (key, row, idx) => {
        switch (key) {
            case "no":
                return <span className="font-semibold text-xs text-[#64748B] dark:text-textsecondary">{idx + 1 + (pagination.pageNumber - 1) * pagination.pageSize}</span>;
            case "employee":
                return (
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-actionitembg border border-slate-200 dark:border-bordercolor flex items-center justify-center text-slate-500 dark:text-textsecondary font-bold text-xs uppercase">
                            {row.employeeName ? row.employeeName.charAt(0) : "U"}
                        </div>
                        <div className="flex flex-col">
                            <span className="font-bold text-xs text-[#000000] dark:text-textprimary">{row?.employeeName || "—"}</span>
                        </div>
                    </div>
                );
            case "checkIn":
                return <span className="font-medium text-xs text-[#475569] dark:text-textprimary">{row?.checkIn || "—"}</span>;
            case "checkOut":
                return <span className="font-medium text-xs text-[#475569] dark:text-textprimary">{row?.checkOut || "—"}</span>;
            case "workHours":
                return <span className="font-medium text-xs text-[#334155] dark:text-textprimary">{row?.workHours || "—"}</span>;
            case "date":
                return <span className="font-semibold text-xs text-slate-600 dark:text-textsecondary">{row?.date || "—"}</span>;
            case "status":
                return (
                    <span className={`px-2.5 py-1 rounded-full text-[9px] font-black tracking-wider uppercase ${getStatusStyles(row?.status)}`}>
                        {row?.status?.replace("_", " ") || "—"}
                    </span>
                );
            default: return "—";
        }
    };

    const tableActions = (
        <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto text-xs">
            <div className="relative flex-1 w-full sm:w-60">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-400">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                </span>
                <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setPagination(prev => ({ ...prev, pageNumber: 1 }));
                    }}
                    placeholder="Search by user, unit..."
                    className="w-full pl-9 pr-4 py-1.5 border border-gray-200 dark:border-bordercolor rounded-lg text-xs outline-none focus:border-indigo-500 transition-colors bg-white dark:bg-actionitembg text-[#475569] dark:text-textprimary"
                />
            </div>
            <div className="flex items-center gap-2">
                <span className="text-slate-500 dark:text-textsecondary font-bold">Start Date:</span>
                <div className="relative flex items-center bg-[#F8FAFC] dark:bg-actionitembg border border-gray-200 dark:border-bordercolor rounded-lg px-3 py-1.5 font-medium text-[#475569] dark:text-textprimary hover:border-gray-400">
                    <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="bg-transparent border-none outline-none font-medium text-[#475569] dark:text-textprimary cursor-pointer text-xs"
                    />
                </div>
            </div>
            <div className="flex items-center gap-2">
                <span className="text-slate-500 dark:text-textsecondary font-bold">End Date:</span>
                <div className="relative flex items-center bg-[#F8FAFC] dark:bg-actionitembg border border-gray-200 dark:border-bordercolor rounded-lg px-3 py-1.5 font-medium text-[#475569] dark:text-textprimary hover:border-gray-400">
                    <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="bg-transparent border-none outline-none font-medium text-[#475569] dark:text-textprimary cursor-pointer text-xs"
                    />
                </div>
            </div>
        </div>
    );

    const handleRowClick = (row) => {
        setSelectedRecord(row);
        setIsDetailModalOpen(true);
    };

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto gap-4 pt-4 scrollbar-hide">
            <Breadcrumbs />
            <PageHeadingSection
                mainHeading="Attendance Monitor"
                subHeading="View and track user punches and daily stats"
            />

            {/* Attendance Analytics Metrics */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
                {metricCardsData.map((card, index) => (
                    <MetricCard
                        key={index}
                        title={card.title}
                        value={card.value}
                        icon={card.icon}
                    />
                ))}
            </div>

            <div className="shrink-0 flex-1 flex flex-col">
                <CommonTable
                    loading={loading}
                    tableHeading="Attendance Logs"
                    headers={tableHeaders}
                    data={filteredAttendance.slice((pagination.pageNumber - 1) * pagination.pageSize, pagination.pageNumber * pagination.pageSize).map((u, index) => {
                        return {
                            ...u,
                            uniqueKey: `row-${index}-${u.attendanceId}`,
                            pageIndex: index
                        };
                    })}
                    renderCell={(key, row) => renderTableCell(key, row, row.pageIndex)}
                    headerAction={tableActions}
                    pagination={{ ...pagination, totalRecords: filteredAttendance.length }}
                    onPageChange={(page) => setPagination(prev => ({ ...prev, pageNumber: page }))}
                    onLimitChange={(limit) => setPagination(prev => ({ ...prev, pageSize: limit, pageNumber: 1 }))}
                    hideScrollbar={false}
                    isLoading={loading}
                    isRowExpandable={() => true}
                    expandedRowRender={(row) => (
                        <div className="bg-[#F8FAFC] dark:bg-actionitembg p-4 rounded-lg flex flex-col md:flex-row gap-6 justify-between text-xs text-slate-600 dark:text-textsecondary border-t border-slate-100 dark:border-bordercolor w-full items-center">
                            <div className="flex-1 flex flex-col gap-4">
                                {/* First Row */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="flex flex-col gap-0.5">
                                        <span className="text-[10px] font-bold text-slate-400 dark:text-textsecondary uppercase tracking-wider">Punch Location</span>
                                        <span className="text-slate-800 dark:text-textprimary font-semibold">{row.location || "—"}</span>
                                    </div>
                                    <div className="flex flex-col gap-0.5">
                                        <span className="text-[10px] font-bold text-slate-400 dark:text-textsecondary uppercase tracking-wider">Unit Name</span>
                                        <span className="text-slate-800 dark:text-textprimary font-semibold">{row.unitName || "—"}</span>
                                    </div>
                                </div>

                                {/* Second Row */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                                    <div className="flex flex-col gap-0.5">
                                        <span className="text-[10px] font-bold text-slate-400 dark:text-textsecondary uppercase tracking-wider">Role</span>
                                        <span className="text-slate-800 dark:text-textprimary font-semibold">{row.role || "Employee"}</span>
                                    </div>
                                    <div className="flex flex-col gap-0.5">
                                        <span className="text-[10px] font-bold text-slate-400 dark:text-textsecondary uppercase tracking-wider">Source</span>
                                        <span className="text-slate-800 dark:text-textprimary font-semibold">{row.punchSource || "—"}</span>
                                    </div>
                                    <div className="flex flex-col gap-0.5">
                                        <span className="text-[10px] font-bold text-slate-400 dark:text-textsecondary uppercase tracking-wider">Early Leave</span>
                                        <span className="text-red-500 font-bold">{row.earlyLeaveMins !== undefined && row.earlyLeaveMins !== null ? `${row.earlyLeaveMins} mins` : "—"}</span>
                                    </div>
                                    <div className="flex flex-col gap-0.5">
                                        <span className="text-[10px] font-bold text-slate-400 dark:text-textsecondary uppercase tracking-wider">Extra Time</span>
                                        <span className="text-emerald-600 font-bold">{row.extraTimeMins !== undefined && row.extraTimeMins !== null ? `${row.extraTimeMins} mins` : "—"}</span>
                                    </div>
                                </div>


                            </div>
                            <button
                                onClick={() => handleRowClick(row)}
                                className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-bordercolor bg-white dark:bg-cardbg text-[#475569] dark:text-textprimary hover:bg-slate-50 dark:hover:bg-actionitembg active:scale-95 transition-all shadow-sm self-center whitespace-nowrap text-xs font-bold"
                            >
                                <FontAwesomeIcon icon={faHistory} />
                                View Full Activity Log
                            </button>
                        </div>
                    )}
                />
            </div>

            {/* Attendance Details Modal */}
            <CommonModal
                isOpen={isDetailModalOpen}
                onClose={() => setIsDetailModalOpen(false)}
                title="Activity Log Details"
                subTitle={`Daily punching timeline for ${selectedRecord?.employeeName || ""}`}
            >
                {selectedRecord && (
                    <div className="space-y-6 pt-2 max-h-[60vh] overflow-y-auto pr-2 scrollbar-hide">


                        {/* Timeline punched logs */}
                        <div className="space-y-4">

                            {selectedRecord.punchHistory?.length > 0 ? (
                                <div className="max-h-44 overflow-y-auto pr-2 border border-slate-100 dark:border-bordercolor rounded-lg p-3 bg-[#F8FAFC] dark:bg-actionitembg">
                                    <div className="border-l-2 border-slate-200 dark:border-bordercolor pl-4 space-y-4 ml-2 pt-1 pb-1">
                                        {selectedRecord.punchHistory.map((punch, idx) => (
                                            <div key={idx} className="relative flex flex-col gap-0.5 text-xs">
                                                {/* dot */}
                                                <div
                                                    className="absolute -left-[23px] top-1 w-2.5 h-2.5 rounded-full border-2 border-white dark:border-cardbg shadow-sm"
                                                    style={{ backgroundColor: (punch.type === "Entry" || punch.type.includes("In")) ? primaryColor() : "#EF4444" }}
                                                />
                                                <div className="flex items-center gap-2 justify-between">
                                                    <strong className="text-slate-800 dark:text-textprimary">{punch.type}</strong>
                                                    <span className="text-slate-400 dark:text-textsecondary font-medium text-[10px]">{punch.time}</span>
                                                </div>
                                                <p className="text-slate-500 dark:text-textsecondary text-[10px]">{punch.device}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <p className="text-xs text-slate-400 dark:text-textsecondary italic">No punches recorded for this date.</p>
                            )}
                        </div>

                        <div className="flex justify-end pt-4">
                            <button
                                type="button"
                                onClick={() => setIsDetailModalOpen(false)}
                                className="px-6 py-2.5 rounded-xl border border-gray-200 dark:border-bordercolor text-[#475569] dark:text-textprimary bg-transparent hover:bg-slate-50 dark:hover:bg-actionitembg transition-colors text-xs font-bold"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                )}
            </CommonModal>
        </div>
    );
};

export default AttendanceList;

